import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Zap, Clock, Mail, Pencil, Play, Square, Trash2, MessageSquare, AlertTriangle, History, Bot,
} from '../../icons';
import {
  Badge, Button, Card, EmptyState, PageHeader, Timeline, type TimelineItem,
} from '../../components/shared';
import { layerConfirm } from '../../components/LayerPopup/LayerPopup';
import {
  listAutomations, patchAutomation, deleteAutomation, listAutomationRuns,
  WEEKDAY_LABEL, RUN_STATUS_LABEL,
  type AiAutomation, type AiAutomationRun, type MailEventFilter,
} from '../../api/assist-automations';
import { listMailAccounts, type MailAccount } from '../../api/external-mail';
import { AI_HOME_PATH, AI_AUTOMATIONS_PATH } from '../../lib/ai-menu';
import './AiShell.css';

/**
 * AI · 업무자동화 — 「매주 월요일에 근태 요약해줘」 (설계 판 X1).
 *
 * ## ★★★ 시각을 «정각»으로 안 묻는다
 *
 * 「매주 월요일 09:00」은 모두가 고르는 값이라 그 1분에 전부 몰린다. 자동화 1만·회차 30초면
 * 필요 동시성이 ∞다(설계 §06 실측). 그래서 **오전/오후/저녁** 창을 고르게 하고 실제 분은
 * 서버가 창 안에 흩어 고정한다. **친절해 보이지만 부하 설계다.**
 *
 * ## ★★★ 「다음 실행」을 화면이 세지 않는다
 *
 * 서버가 **회사 시간대**로 세어 `nextRunAt` 을 준다. 화면이 시간대를 다시 풀면 둘이 어긋나고,
 * 그때 「화면은 9시라는데 10시에 온다」가 된다.
 *
 * ## ★★ X1 은 «읽기 전용»이다
 *
 * 파일을 만들거나 메일을 보내는 자동화는 「사람이 안 보는데 누구 명의로 쓰나」가 열린다.
 * 그 답이 나오기 전에는 열지 않는다 — 그래서 폼에 «무엇을 할까» 칸이 없고 **묻는 말**만 있다.
 *
 * ## ★★★ 방아쇠가 둘이 됐다 (판 X2)
 *
 * 「때가 되면」과 「새 메일이 오면」이다. 두 부류는 **필요한 칸이 서로 없다** — 사건에는 요일도
 * 시간대도 없고, 시각에는 조건이 없다. 그래서 «있는 칸을 회색으로» 두지 않고 **아예 안 그린다**:
 * 못 쓰는 칸을 보여 주면 사람은 그것이 왜 안 먹는지를 먼저 궁금해한다.
 *
 * ★★ **무엇이 AI 에게 가는지 적는다.** 새 메일 자동화는 제목·보낸사람·첨부 이름을 넘기고, 본문은
 *    AI 가 필요할 때 읽는다 — 연동해 둔 **개인 외부 메일도 포함**된다. 사람이 그 사실을 알고
 *    켜야 하는 자리라 폼에 한 줄로 적었다(설계 §10-01).
 *
 * ## ★ 결과는 «전용 대화»에 쌓인다
 *
 * 회차마다 새 대화를 만들면 목록이 몇 주 만에 쓰레기가 된다. 자동화 하나에 대화 하나이고,
 * 「결과 보기」가 그 대화로 간다(첫 회차 전에는 갈 곳이 없어 안 그린다).
 */



/** 「어떤 메일이 깨우나」를 한 줄로 — 조건이 없으면 «전부»라고 **말한다**(빈칸으로 두지 않는다). */
export function describeMailFilter(
  f: MailEventFilter | null,
  accounts: readonly MailAccount[] = [],
): string {
  const parts: string[] = [];
  /* ★ 사서함을 «먼저» 적는다 — 「어디로 왔나」가 「누가 보냈나」보다 먼저 읽힌다. */
  if (f?.accountIds && f.accountIds.length > 0) {
    const names = f.accountIds.map((id) => accounts.find((a) => a.id === id)?.email ?? `계정 ${id}`);
    parts.push(names.length === 1 ? `${names[0]} 로 온 것` : `${names[0]} 외 ${names.length - 1}곳으로 온 것`);
  }
  if (f?.from && f.from.length > 0) {
    parts.push(f.from.length === 1 ? `${f.from[0]} 이(가) 보낸 것` : `${f.from[0]} 외 ${f.from.length - 1}명이 보낸 것`);
  }
  if (f?.subjectContains) parts.push(`제목에 「${f.subjectContains}」`);
  if (f?.hasAttachment) parts.push('첨부 있는 것만');
  return parts.length > 0 ? parts.join(' · ') : '모든 새 메일';
}

function describe(a: AiAutomation, accounts: readonly MailAccount[] = []): string {
  if (a.triggerKind === 'EVENT') return `새 메일이 올 때 · ${describeMailFilter(a.eventFilter, accounts)}`;
  return `매주 ${a.weekday === null ? '?' : WEEKDAY_LABEL[a.weekday] ?? '?'}요일 ${a.windowLabel ?? ''}`;
}

/**
 * ★★★ **회사 관리자가 끈 자동화는 내가 못 켠다** (2026-09-11 사용자 결정 · 설계 §07).
 *
 * 서버가 막지만(409), 화면이 **누를 수 있게 두면** 사람은 누르고 나서야 안 된다는 것을 안다 —
 * 「할 수 없는 일을 세워 두면 그게 잘못된 안내」다. 그래서 단추를 잠그고 **왜인지 적는다**.
 *
 * ⚠ 지금은 **사람이 읽는 말이 곧 표지**다(표에 「누가 껐나」 칸이 없다 —
 *   `lib/ai-automation-admin-hold.ts` 의 같은 사정). 문구가 바뀌면 여기와 서버가 함께 바뀌어야
 *   하므로 **검사가 두 곳의 문구를 못 박는다.** 칸이 생기면 이 함수의 속만 바뀐다.
 */
export const ADMIN_PAUSED_REASON = '회사 관리자가 멈췄습니다.';

/**
 * ★★★ **판정은 서버가 한다**(migration 190 · `paused_at` 칸).
 *
 * 처음에는 화면이 사유 문자열을 보고 판정했다 — 문구가 바뀌면 **조용히 어긋나는** 자리였다.
 * 이제 서버가 `adminHeld` 로 말해 주므로 화면은 그것을 그대로 믿는다. 규칙이 두 곳에 있으면
 * 언젠가 갈라지고, 그때 「화면은 잠겼다는데 저장은 된다」가 된다.
 */
export function isAdminHeld(a: Pick<AiAutomation, 'adminHeld'>): boolean {
  return a.adminHeld;
}

/** 「누가 껐나」 — 모르면 이름 없이 말한다(지어내지 않는다). */
export function heldByText(a: Pick<AiAutomation, 'pausedByName'>): string {
  return a.pausedByName !== null
    ? `${a.pausedByName} 님(회사 관리자)이 멈췄습니다. 다시 켜려면 그분께 문의해 주세요.`
    : '회사 관리자가 멈췄습니다. 다시 켜려면 회사 관리자에게 문의해 주세요.';
}

function whenText(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${d.getMonth() + 1}월 ${d.getDate()}일 (${WEEKDAY_LABEL[d.getDay()]}) ${
    String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * 회차 한 줄을 «지나온 일» 로 옮긴다 (정본 E9 Timeline).
 *
 * ★★★ **「안 돌았다」·「건너뛰었다」·「실패했다」는 다른 말이다.** 표에 상태가 다섯인 것이
 *   그래서인데, 화면이 전부 「—」로 그리면 그 구별이 통째로 사라진다.
 * ★ 사유는 **서버가 이미 사람 말로 적어 둔다**(「하루 24회 한도를 넘어 건너뛰었습니다」…) —
 *   여기서 다시 지어내지 않고 그대로 보여 준다.
 */
export function runToTimelineItem(r: AiAutomationRun): TimelineItem {
  const tone = r.status === 'OK' ? 'success'
    : r.status === 'FAILED' ? 'danger'
    : r.status === 'SKIPPED' ? 'warning'
    : 'info';
  /* 「메일 3통이 깨웠다」는 사건 자동화에만 있다 — 시각 자동화는 null 이라 안 적는다. */
  const woke = r.eventCount !== null && r.eventCount > 0 ? ` · 메일 ${r.eventCount}통` : '';
  const at = r.finishedAt ?? r.scheduledFor;
  return {
    id: r.id,
    tone,
    title: `${RUN_STATUS_LABEL[r.status]}${woke}`,
    meta: r.error ?? undefined,
    at,
    atLabel: whenText(at),
  };
}

export function AiAutomationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<AiAutomation[]>([]);
  const [limit, setLimit] = useState(5);
  const [timezone, setTimezone] = useState<string | null>(null);
  /* ★ 「10분」·「24회」를 화면이 따로 적지 않는다 — 서버가 준 값을 그대로 말한다. */
  /**
   * 연동해 둔 외부 메일 계정 — 「어느 사서함으로 온 것만」을 고르려면 이름이 있어야 한다.
   * ★ 못 읽어도 화면은 돈다(고르기만 안 뜬다) — 자동화 목록이 메일 설정에 매이지 않게.
   */
  const [accounts, setAccounts] = useState<MailAccount[]>([]);
  /**
   * 고를 수 있는 에이전트.
   *
   * ★ **살아 있는 것만 담는다.** 목록 API 는 관리자에게 중지된 전사 에이전트도 준다(다시 켜야
   *   하니까) — 그러나 **붙일 수는 없다**(서버의 `findUsableAgent` 가 그렇게 정한다).
   *   고를 수 없는 것을 고르게 두면 저장 때 400 을 받고서야 알게 된다.
   * ★ 못 읽어도 화면은 돈다 — 고르기 칸만 안 뜬다(자동화가 에이전트 설정에 매이지 않게).
   */
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** 이력을 펼친 자동화 id — 한 번에 하나만 편다(여러 개를 펴면 카드가 통째로 길어진다). */
  const [runsFor, setRunsFor] = useState<string | null>(null);
  const [runs, setRuns] = useState<AiAutomationRun[] | 'loading' | 'failed'>('loading');

  /** null = 폼 닫힘 · '' = 새로 만들기 · id = 그것을 고치는 중 */

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const r = await listAutomations();
      setItems(r.automations);
      setLimit(r.limit);
      setTimezone(r.timezone);
    } catch { setFailed(true); } finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => {
    void listMailAccounts().then(setAccounts).catch(() => { /* 덤이다 — 없으면 고르기만 안 뜬다. */ });
  }, []);

  const activeCount = items.filter((a) => a.status === 'ACTIVE').length;
  /*
   * ★★ 시간대는 **시각 자동화의 사정**이다. 없다고 「새 메일이 오면」까지 막으면
   *    돌 수 있는 것을 못 만들게 하는 것이다 — 그래서 만들기 단추는 열어 두고 부류만 막는다.
   */
  const canCreate = activeCount < limit;




  /** 이력 펴기·접기 — 펼 때만 읽는다(목록을 그릴 때마다 회차까지 부르지 않는다). */
  async function toggleRuns(a: AiAutomation): Promise<void> {
    if (runsFor === a.id) { setRunsFor(null); return; }
    setRunsFor(a.id);
    setRuns('loading');
    try { setRuns(await listAutomationRuns(a.id)); } catch { setRuns('failed'); }
  }

  async function toggle(a: AiAutomation): Promise<void> {
    try {
      await patchAutomation(a.id, { status: a.status === 'ACTIVE' ? 'PAUSED' : 'ACTIVE' });
      await load();
    } catch (e) {
      /* ★ 서버가 «왜» 막았는지 말해 주면 그대로 전한다 — 「바꾸지 못했습니다」로 뭉뚱그리면
           관리자가 끈 것인지 그냥 실패한 것인지 알 수 없다. */
      const msg = (e as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg ?? '상태를 바꾸지 못했습니다.');
    }
  }

  async function remove(a: AiAutomation): Promise<void> {
    /* ★★★ 되돌릴 수 없다 — 먼저 묻는다.
         ⚠ 예전 문구는 「지나간 결과는 대화에 그대로 남습니다」였는데 **이제 거짓**이다.
           사용자 결정(2026-09-21)으로 봇방·지난 결과까지 **전부** 지운다. 문구가 동작보다
           오래 사는 일이 없도록 여기 같이 고친다. */
    const ok = await layerConfirm(
      `「${a.name}」이(가) 더 이상 돌지 않습니다.\n채팅의 「${a.name}」 봇방과 지금까지 받은 결과도 함께 삭제됩니다.`,
      { title: '자동화를 지울까요?', confirmText: '지우기', danger: true },
    );
    if (!ok) return;
    try { await deleteAutomation(a.id); await load(); }
    catch { setError('지우지 못했습니다.'); }
  }

  return (
    <div className="aiagents">
      <PageHeader
        breadcrumb={['AI', '업무자동화']}
        title="업무자동화"
        description="정해 둔 때가 되면 AI 가 스스로 묻고, 답을 전용 대화에 남깁니다."
        actions={
          <Button
            variant="primary"
            iconLeft={<Plus size={15} />}
            disabled={!canCreate}
            onClick={() => navigate(`${AI_AUTOMATIONS_PATH}/new`)}
          >
            자동화 만들기
          </Button>
        }
        help={{
          body:
            '자동화는 «만든 사람 명의»로 읽습니다 — 내가 볼 수 없는 자료는 자동화도 못 봅니다. '
            + '지금은 읽고 요약하는 일만 합니다(파일을 만들거나 메일을 보내지 않습니다).',
        }}
      />

      <div className="aiagents__col">
        {/* ★★ 「왜 못 만드나」를 말한다 — 안 적으면 단추가 죽은 이유를 알 수 없다. */}
        {timezone === null && !loading && (
          <p className="aiagents__note" role="status">
            <AlertTriangle size={14} aria-hidden />{' '}
            회사 시간대가 정해져 있지 않아 <b>정해진 때에 도는 자동화</b>는 만들 수 없습니다 —
            「월요일 오전」이 언제인지 정할 수 없기 때문입니다. 관리자에게 문의해 주세요.
            (「새 메일이 왔을 때」는 시계가 없어도 됩니다.)
          </p>
        )}
        {activeCount >= limit && (
          <p className="aiagents__note" role="status">
            켜 둘 수 있는 자동화 {limit}개를 다 썼습니다. 쓰지 않는 것을 멈추거나 지우면 더 만들 수 있습니다.
          </p>
        )}
        {error !== null && <p className="aiagents__note" role="alert">{error}</p>}


        {loading ? (
          <p className="aiagents__note">불러오는 중입니다…</p>
        ) : failed ? (
          <p className="aiagents__note" role="alert">목록을 불러오지 못했습니다. 잠시 후 다시 시도해 주세요.</p>
        ) : items.length === 0 ? (
          <EmptyState
            icon={Zap}
            title="아직 자동화가 없습니다"
            description="같은 것을 매주 묻고 있다면, 그 일을 자동화로 옮겨 둘 수 있습니다."
            action={
              <Button variant="primary" iconLeft={<Plus size={15} />} disabled={!canCreate} onClick={() => navigate(`${AI_AUTOMATIONS_PATH}/new`)}>
                자동화 만들기
              </Button>
            }
          />
        ) : (
          <ul className="aiagents__list">
            {items.map((a) => (
              <li key={a.id}>
                <Card headless className={`aiagents__card${a.status !== 'ACTIVE' ? ' is-stopped' : ''}`}>
                  <div className="aiagents__head">
                    <span className="aiagents__nm">{a.name}</span>
                    {a.status === 'ACTIVE'
                      ? <Badge variant="info" size="sm">켜짐</Badge>
                      : <Badge variant="warning" size="sm">멈춤</Badge>}
                  </div>

                  {/* ★ 멈춘 이유를 «적는다» — 연속 실패로 서버가 멈춘 것일 수 있다.
                      ★★ 관리자가 잠근 것이면 **누가 껐는지**까지 적는다(2026-09-11 사용자 결정) —
                         「회사 관리자」라고만 하면 누구에게 물어야 할지 모른다. */}
                  {isAdminHeld(a)
                    ? <p className="aiagents__desc">{heldByText(a)}</p>
                    : a.pausedReason && <p className="aiagents__desc">{a.pausedReason}</p>}

                  <div className="aiagents__meta">
                    <span className="aiagents__metaitem">
                      {a.triggerKind === 'EVENT'
                        ? <Mail size={13} aria-hidden />
                        : <Clock size={13} aria-hidden />}
                      {describe(a, accounts)}
                    </span>
                    {/* ★ 사건 자동화에는 «다음 실행»이 없다 — 「—」로 비워 두면 고장처럼 읽힌다. */}
                    <span className="aiagents__metaitem">
                      {a.triggerKind === 'EVENT'
                        ? '메일이 올 때 돕니다'
                        : `다음 실행 ${a.status === 'ACTIVE' ? whenText(a.nextRunAt) : '—'}`}
                    </span>
                    {a.lastRunAt && (
                      <span className="aiagents__metaitem">마지막 {whenText(a.lastRunAt)}</span>
                    )}
                    {/*
                      * ★★ 붙은 에이전트를 적는다. **중지된 것이면 그 사실도** 적는다 —
                      *   워커는 중지된 에이전트를 통째로 없는 것으로 치고 돌기 때문에,
                      *   안 적으면 「에이전트를 붙였는데 왜 그냥 답하지」가 된다.
                      */}
                    {a.agentName !== null && (
                      <span className="aiagents__metaitem">
                        <Bot size={13} aria-hidden />
                        {a.agentName}
                        {a.agentUsable === false && ' (중지됨 — 역할 없이 돕니다)'}
                      </span>
                    )}
                  </div>

                  <div className="aiagents__acts">
                    {/* 첫 회차 전에는 갈 곳이 없다 — 없는 대화로 보내지 않는다. */}
                    {a.conversationId && (
                      <Button
                        size="sm"
                        variant="primary"
                        iconLeft={<MessageSquare size={13} />}
                        onClick={() => navigate(AI_HOME_PATH)}
                      >
                        결과 보기
                      </Button>
                    )}
                    {/* ★★ 「돌았나」를 볼 자리 — 여태 회차는 표에만 있고 보는 화면이 0이었다. */}
                    <Button
                      size="sm"
                      variant="ghost"
                      iconLeft={<History size={13} />}
                      onClick={() => { void toggleRuns(a); }}
                      aria-expanded={runsFor === a.id}
                    >
                      이력
                    </Button>
                    <Button size="sm" variant="ghost" iconLeft={<Pencil size={13} />} onClick={() => navigate(`${AI_AUTOMATIONS_PATH}/${a.id}`)}>
                      수정
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      /* ★ 관리자가 끈 것은 내가 못 켠다 — 누를 수 있게 두면 눌러 보고서야 안다. */
                      disabled={isAdminHeld(a)}
                      title={isAdminHeld(a) ? heldByText(a) : undefined}
                      iconLeft={a.status === 'ACTIVE' ? <Square size={13} /> : <Play size={13} />}
                      onClick={() => void toggle(a)}
                    >
                      {a.status === 'ACTIVE' ? '멈춤' : '다시 시작'}
                    </Button>
                    <Button size="sm" variant="ghost" iconLeft={<Trash2 size={13} />} onClick={() => void remove(a)}>
                      삭제
                    </Button>
                  </div>

                  {/* 펼친 이력 — 「지나온 일」이므로 최신이 위다(정본 E9). */}
                  {runsFor === a.id && (
                    <div className="aiauto__runs">
                      {runs === 'loading' ? (
                        <p className="aiagents__note">이력을 불러오는 중입니다…</p>
                      ) : runs === 'failed' ? (
                        <p className="aiagents__note" role="alert">이력을 불러오지 못했습니다.</p>
                      ) : runs.length === 0 ? (
                        /* ★★★ **「아직 안 돌았다」와 「돌았는데 아무 일도 없었다」는 다른 말이다.**
                             0 을 「없다」로 그리지 않는다 — 무엇을 기다리면 되는지도 적는다. */
                        <p className="aiagents__note">
                          아직 한 번도 돌지 않았습니다.
                          {a.triggerKind === 'EVENT'
                            ? ' 조건에 맞는 메일이 들어오면 그때 돕니다.'
                            : ' 정해 둔 때가 되면 돕니다.'}
                        </p>
                      ) : (
                        <>
                          <Timeline items={runs.map(runToTimelineItem)} />
                          {/* 서버가 최근 20회만 준다 — 그 사실을 «적는다»(더 있는지 모르게 두지 않는다). */}
                          {runs.length >= 20 && (
                            <p className="aiagents__note">최근 20회만 보여 드립니다.</p>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
